import { useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import {
  Brand,
  CustomerIdentity,
  CustomerIdentityRequest,
  VehicleDetailsRequest
} from "../../modules/entities/Entities";
import { resetApplicationContext } from '../../utils/ApplicationContextUtils';
import { LoginPageContext } from './LoginPageContext';
import { LoginPageFormData } from './LoginPageFormData';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { PersonalIdentityType } from '../../modules/login/PersonalIdentityType';
import { selectedIdentity } from '../../modules/login/PersonalIdentity';
import { useBrandNavigate } from '../../modules/navigation/UseBrandNavigate';
import { PageRoutes } from '../../modules/navigation/PageRoutes';
import { useGetCustomerIdentity, useGetVehicleDetails } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { LoginViewProps } from './LoginPageViewProps';
import { useTracking } from '../../tracking';
import { Steps } from "../../modules/wizard/Steps";
import { BrandState, useBrand } from "../../modules/contexts/brand-context/BrandProvider";
import { useModalManager } from "../../modules/contexts/modal-manager-context/ModalManagerProvider";
import { NoVwVehicleModalContent } from "../../modules/modal/NoVwVehicleModalContent/NoVwVehicleModalContent";
import { isServiceUnavailable } from '../../modules/async/api-client';

function createVehicleDetailsRequest(identity: CustomerIdentity): VehicleDetailsRequest {
  return {
    customerId: identity.customerId,
    vin: identity.vin,
  };
}

export function ContextAccessor({ nationalIdNumber, passportNumber, registrationNumber }: BookingState): LoginPageContext {
  return {
    nationalIdNumber,
    passportNumber,
    registrationNumber,
  };
}

export const LoginPage = withPageContext(ContextAccessor, () => {
  const viewProvider = useViewProvider();
  const modalManager = useModalManager();
  const [, setBookingState] = useBookingContext();
  const { setBrand } = useBrand();
  const [customerIdentityRequest, setCustomerIdentityRequest] = useState<CustomerIdentityRequest | null>(null);
  const [vehicleDetailsRequest, setVehicleDetailsRequest] = useState<VehicleDetailsRequest | null>(null);
  // The form always starts empty. It used to be prefilled from the stored booking, which showed
  // the previous customer's ID and registration number to whoever opened the page next.
  const [initialFormData] = useState<LoginPageFormData>(() => ({
    identity: {
      identityType: PersonalIdentityType.CustomerId,
      customerId: '',
      passportNumber: '',
    },
    registrationNumber: '',
  }));
  const [formData, setFormData] = useState<LoginPageFormData>();
  const navigate = useBrandNavigate();
  const trackingService = useTracking();

  useGetCustomerIdentity(customerIdentityRequest, {
    onSuccess(customerIdentity) {
      if (!customerIdentity) {
        navigate(PageRoutes.LoginError);
        return;
      }

      setVehicleDetailsRequest(createVehicleDetailsRequest(customerIdentity));
    },
    onError(error) {
      // Backend unreachable or timing out — let the global modal explain; don't pretend it was a login error.
      if (isServiceUnavailable(error)) {
        return;
      }
      navigate(PageRoutes.LoginError);
    },
    skipGlobalError: true,
  });

  useGetVehicleDetails(vehicleDetailsRequest, {
    onSuccess: (vehicleDetails) => {
      if (!vehicleDetails) {
        navigate(PageRoutes.LoginError);
        return;
      }

      if (vehicleDetails.brand === Brand.Audi) {
        modalManager.open({
          title: 'Vehicle not found',
          content: NoVwVehicleModalContent,
          actions: [
            {
              title: 'OK',
              type: 'primary',
              key: 'ok',
              onClick(close) {
                close();
              },
            },
          ],
        });
        return;
      }

      const state: BookingState = {
        ...resetApplicationContext(),
        nationalIdNumber: selectedIdentity(formData!.identity).customerId,
        passportNumber: selectedIdentity(formData!.identity).passportNumber,
        registrationNumber: formData!.registrationNumber,
        vehicleDetails,
      };

      setBookingState(state);
      // The vehicle is found, so the customer goes straight on. A lookup of existing bookings
      // used to sit in between: its result has not been used since the bookings page was
      // switched off, yet its failure kept the customer on the login page. The brand comes
      // from the response — the booking state in this closure is still the previous one —
      // and is never Audi here: Audi vehicles were turned away above.
      setBrand(BrandState.Vw);
      navigate(PageRoutes.VehicleDetails, vehicleDetails.brand);
    },
    onError(error) {
      if (isServiceUnavailable(error)) {
        return;
      }
      navigate(PageRoutes.LoginError);
    },
    skipGlobalError: true,
  });


  const onSubmit = (data: LoginPageFormData): void => {
    const identityLabel = data.identity.identityType === PersonalIdentityType.CustomerId
      ? 'National ID number'
      : 'Passport number';
    trackingService.trackStepButtonClick({
      contentId: 'Next',
      step: Steps.Identification,
      identityLabel: identityLabel,
    });
    setFormData({
      identity: data.identity,
      registrationNumber: data.registrationNumber,
    });
    setCustomerIdentityRequest({
      ...selectedIdentity(data.identity),
      registrationNumber: data.registrationNumber,
    });
  };

  const viewProps: LoginViewProps = {
    initialFormData,
    onSubmit,
  };

  return viewProvider.renderLoginPage(viewProps);
});
